// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import dayjs from 'dayjs';
import { z } from 'zod';

/**
 * Account product types supported by manual bulk upload (`account_type` wire
 * field).
 */
export enum BankAccountType {
  Checking = 'checking',
  Savings = 'savings',
  Investment = 'investment',
  Card = 'card',
}

export const bankAccountTypeSchema = z.enum(BankAccountType);

/**
 * Account owner name captured in the UI.
 *
 * `role` is not collected; upload maps every owner with `role: ""`.
 */
export const bankAccountOwnerSchema = z.object({
  name: z.string().min(1),
});

export type BankAccountOwner = z.infer<typeof bankAccountOwnerSchema>;

/**
 * Optional code number / IBAN in form state. Use `''` when unset / unlinked; wire
 * payloads map empty values to `null` via {@link toWireOptionalIban}.
 */
export const optionalBankIbanSchema = z.string();

export type OptionalBankIban = z.infer<typeof optionalBankIbanSchema>;

/**
 * Account row captured in the upload wizard.
 *
 * @remarks
 * UI form shape (camelCase). Maps 1:1 to {@link AdvisoryAccountsCitecAccount} via
 * {@link buildBulkUploadAccountData}.
 * @example
 *
 * ```ts
 * import {
 *   BankAccountType,
 *   bankAccountSchema,
 * } from '@client/banks/types/account';
 *
 * const account = bankAccountSchema.parse({
 *   iban: 'ES9121000418450200051332',
 *   name: 'Main checking',
 *   currency: 'EUR',
 *   accountType: BankAccountType.Checking,
 *   available: 1500,
 *   balanceDate: '2024-06-01',
 *   owners: [{ name: 'Jane Doe' }],
 * });
 * ```
 */
export const bankAccountSchema = z.object({
  iban: optionalBankIbanSchema,
  name: z.string().trim().min(1),
  currency: z.string().min(1),
  accountType: bankAccountTypeSchema,
  available: z.number().nullable().optional(),
  balanceDate: z
    .string()
    .nullable()
    .optional()
    .check(({ value, issues }) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      const parsed = dayjs(value);

      if (parsed.isValid() && parsed.isAfter(dayjs(), 'day')) {
        issues.push({
          code: 'custom',
          input: value,
          message: 'Date cannot be in the future',
        });
      }
    }),
  owners: z.array(bankAccountOwnerSchema),
});

/** Liquidity account created inline to link an instrument — code number required. */
export const bankAccountLinkedCreateSchema = bankAccountSchema.extend({
  iban: z.string().trim().min(1),
});

export type BankAccount = z.infer<typeof bankAccountSchema>;
export type BankAccountLinkedCreate = z.infer<typeof bankAccountLinkedCreateSchema>;
